import { ArrowUpRight, ClipboardList, Globe2, Hexagon, Scale, UserPlus } from "lucide-react";
import Reveal from "../components/Reveal";
import { scrollToId } from "../lib/scroll";

const SERVICES = [
    {
        icon: Hexagon,
        title: "Employer of Record Services",
        span: "lg:col-span-4",
        body: "An Employer of Record (EOR) arrangement allows your business to engage employees in supported markets without establishing a local entity. Hexagon helps US businesses evaluate and coordinate EOR solutions covering employment administration, onboarding, and ongoing workforce management.",
        points: [
            "EOR services for US companies",
            "Supported-market employment arrangements",
            "Clear process from first call to setup",
        ],
    },
    {
        icon: Globe2,
        title: "International Employment",
        span: "lg:col-span-2",
        body: "Administrative support for engaging talent across borders. We help you understand employment options in the markets we support — primarily the United States, with additional availability in Canada.",
    },
    {
        icon: ClipboardList,
        title: "Workforce Administration",
        span: "lg:col-span-2",
        body: "Ongoing employment administration for your team members: employee records, documentation, payroll administration coordination, and day-to-day workforce management support.",
    },
    {
        icon: Scale,
        title: "Legal & Tax Services",
        span: "lg:col-span-2",
        body: "Legal and tax services connected to international employment, helping your business understand and address the obligations that come with engaging talent across borders.",
    },
    {
        icon: UserPlus,
        title: "Employee Onboarding",
        span: "lg:col-span-2",
        body: "Structured onboarding for internationally engaged employees — documentation collection, employment paperwork, and orientation support — so new team members start smoothly and your records stay organized from day one.",
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="bg-mist py-24 lg:py-32"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="max-w-2xl">
                    <Reveal>
                        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-royal">
                            <span className="h-px w-10 bg-gold" aria-hidden="true" />
                            What We Do
                        </p>
                        <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                            Employment services, handled with care
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-navy/65 sm:text-lg">
                            Hexagon focuses on the administrative side of
                            international employment, so your business can
                            evaluate supported hiring arrangements with clear
                            information and a straightforward process.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-14 grid gap-5 lg:grid-cols-6">
                    {SERVICES.map((service, i) => (
                        <Reveal
                            key={service.title}
                            delay={i * 0.08}
                            className={service.span}
                        >
                            <article
                                data-testid={`service-card-${i}`}
                                className="group flex h-full flex-col rounded-2xl border border-navy/10 border-b-2 bg-white p-8 shadow-sm transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-b-gold hover:shadow-md lg:p-9"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-gold">
                                    <service.icon size={22} strokeWidth={1.8} aria-hidden="true" />
                                </div>
                                <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-navy sm:text-2xl">
                                    {service.title}
                                </h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-navy/65">
                                    {service.body}
                                </p>
                                {service.points && (
                                    <ul className="mt-5 space-y-2">
                                        {service.points.map((point) => (
                                            <li
                                                key={point}
                                                className="flex items-start gap-2.5 text-sm font-medium text-navy/80"
                                            >
                                                <svg
                                                    width="8"
                                                    height="8"
                                                    viewBox="0 0 10 10"
                                                    className="mt-1.5 shrink-0"
                                                    aria-hidden="true"
                                                >
                                                    <path d="M5 0 10 5 5 10 0 5Z" fill="#C8A96B" />
                                                </svg>
                                                {point}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </article>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.15} className="mt-10">
                    <button
                        type="button"
                        data-testid="services-contact-link"
                        onClick={() => scrollToId("contact")}
                        className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-royal transition-colors duration-200 hover:text-navy"
                    >
                        Not sure which service fits? Speak with our team
                        <ArrowUpRight size={16} aria-hidden="true" />
                    </button>
                </Reveal>
            </div>
        </section>
    );
}
